/**
 * Bottom action bar. Fold / check-call / raise + sizing slider + presets
 * + all-in. The shell is ALWAYS rendered — disabled when it's not the
 * hero's turn (or between-hands) so the UI never collapses mid-hand.
 *
 * V2.2: all amounts show BB subtitles. The slider snaps to half-BB
 * increments so the user drags in meaningful steps. Call/Raise CTAs
 * show the raw chip count prominently with the BB conversion below.
 */
import { useEffect, useState } from 'react';
import { BbSubtitle } from '@/components/bb/BbSubtitle';
import { findLegal } from '@/engine/rules';
import type { Action, LegalAction } from '@/engine/types';
import { snapRaiseToBB, toBB } from '@/utils/bb';

export type ActionBarProps = {
  legals: ReadonlyArray<LegalAction>;
  playerId?: string;
  pot?: number;
  /** BB size — drives slider step + amount subtitles. */
  bbSize?: number;
  onAction?: (action: Action) => void;
  disabled?: boolean;
};

const SIZE_PRESETS: ReadonlyArray<{ label: string; fraction: number }> = [
  { label: '1/3', fraction: 1 / 3 },
  { label: '1/2', fraction: 1 / 2 },
  { label: '2/3', fraction: 2 / 3 },
  { label: 'Pot', fraction: 1 },
];

export function ActionBar({
  legals,
  playerId,
  pot,
  bbSize = 0,
  onAction,
  disabled,
}: ActionBarProps) {
  const foldable = findLegal(legals, 'fold');
  const checkable = findLegal(legals, 'check');
  const callable = findLegal(legals, 'call');
  const betable = findLegal(legals, 'bet');
  const raisable = findLegal(legals, 'raise');
  const allinable = findLegal(legals, 'allin');

  const aggressive = raisable ?? betable;
  const minRaise = aggressive?.min ?? 0;
  const maxRaise = aggressive?.max ?? 0;
  const [raiseTo, setRaiseTo] = useState<number>(minRaise);

  useEffect(() => {
    setRaiseTo(minRaise);
  }, [minRaise]);

  const emit = (action: Action) => {
    if (disabled) return;
    onAction?.(action);
  };

  const handlePreset = (fraction: number) => {
    if (!aggressive || !playerId || disabled) return;
    const currentPot = pot ?? 0;
    const target = Math.max(minRaise, Math.min(maxRaise, Math.round(currentPot * fraction)));
    setRaiseTo(target);
  };

  const submitAggressive = () => {
    if (!aggressive || !playerId || disabled) return;
    if (aggressive.type === 'raise') {
      emit({ type: 'raise', playerId, to: raiseTo });
    } else {
      emit({ type: 'bet', playerId, amount: raiseTo });
    }
  };

  const handleSliderChange = (v: number) => {
    if (bbSize > 0) {
      const snapped = snapRaiseToBB(v, bbSize);
      setRaiseTo(Math.max(minRaise, Math.min(maxRaise, snapped)));
    } else {
      setRaiseTo(v);
    }
  };

  const canFold = !!foldable && !!playerId && !disabled;
  const canCheckCall = !!(checkable || callable) && !!playerId && !disabled;
  const canRaise = !!aggressive && !!playerId && !disabled;
  const canAllin = !!allinable && !!playerId && !disabled;
  const rangeInteractive = canRaise;
  const sliderMax = Math.max(maxRaise, minRaise, 1);
  const sliderStep = bbSize > 0 ? Math.max(1, Math.round(bbSize / 2)) : 1;
  const presetDisabled = !rangeInteractive || !pot || pot <= 0;

  const callAmount = callable?.amount ?? 0;
  const allinAmount = allinable?.amount ?? 0;

  return (
    <footer
      className={`flex flex-col gap-1.5 bg-bg-elev/90 backdrop-blur border-t-2 px-3 py-1.5 transition-colors ${
        disabled ? 'border-border' : 'border-accent-alt/50'
      }`}
      data-testid="action-bar"
      data-disabled={disabled ? 'true' : undefined}
      aria-disabled={disabled || undefined}
    >
      <div className="flex items-center gap-2" data-testid="sizing-row">
        {SIZE_PRESETS.map((preset) => (
          <button
            type="button"
            key={preset.label}
            className="flex-1 text-xs font-display uppercase tracking-wider rounded-sm bg-bg border-2 border-border px-1 py-1.5 text-muted enabled:hover:text-accent-alt enabled:hover:border-accent-alt/60 disabled:opacity-30 transition-colors"
            onClick={() => handlePreset(preset.fraction)}
            disabled={presetDisabled}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          className="flex-1 text-xs font-display uppercase tracking-wider rounded-sm bg-bg border-2 border-warning/60 px-1 py-1.5 text-warning enabled:hover:bg-warning/15 disabled:opacity-30 transition-colors"
          onClick={() => setRaiseTo(maxRaise)}
          disabled={presetDisabled}
        >
          Max
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          className="flex-1 accent-accent-alt disabled:opacity-40 cursor-pointer"
          min={minRaise}
          max={sliderMax}
          value={raiseTo}
          step={sliderStep}
          disabled={!rangeInteractive}
          onChange={(e) => handleSliderChange(Number(e.target.value))}
          aria-label="레이즈 사이즈"
          data-testid="raise-slider"
        />
        <div className="flex flex-col items-end">
          <input
            type="number"
            className="w-24 text-right bg-bg border-2 border-border rounded-sm px-2 py-1 num text-base text-fg focus:border-accent-alt focus:outline-none disabled:opacity-40"
            min={minRaise}
            max={sliderMax}
            value={raiseTo}
            step={sliderStep}
            disabled={!rangeInteractive}
            onChange={(e) => handleSliderChange(Number(e.target.value))}
            aria-label="레이즈 금액"
            data-testid="raise-amount"
          />
          <BbSubtitle chips={raiseTo} bbSize={bbSize} tone="muted" className="mt-0.5" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="action-buttons">
        <button
          type="button"
          className="relative rounded-sm py-2 font-display uppercase tracking-widest border-2 border-danger/80 bg-danger/15 text-danger enabled:hover:bg-danger/25 enabled:active:translate-y-0.5 disabled:opacity-30 disabled:border-border disabled:bg-bg disabled:text-muted transition-all shadow-[3px_3px_0_0_hsl(var(--color-danger)/0.6)] enabled:hover:shadow-[4px_4px_0_0_hsl(var(--color-danger)/0.6)] enabled:active:shadow-[1px_1px_0_0_hsl(var(--color-danger)/0.4)] disabled:shadow-none flex flex-col items-center justify-center gap-0.5"
          disabled={!canFold}
          onClick={() => playerId && emit({ type: 'fold', playerId })}
          data-testid="action-fold"
        >
          <span className="text-sm">Fold</span>
        </button>
        <button
          type="button"
          className="relative rounded-sm py-2 font-display uppercase tracking-widest border-2 border-accent-alt/80 bg-accent-alt/15 text-accent-alt enabled:hover:bg-accent-alt/25 enabled:active:translate-y-0.5 disabled:opacity-30 disabled:border-border disabled:bg-bg disabled:text-muted transition-all shadow-[3px_3px_0_0_hsl(var(--color-accent-alt)/0.6)] enabled:hover:shadow-[4px_4px_0_0_hsl(var(--color-accent-alt)/0.6)] enabled:active:shadow-[1px_1px_0_0_hsl(var(--color-accent-alt)/0.4)] disabled:shadow-none flex flex-col items-center justify-center gap-0.5"
          disabled={!canCheckCall}
          onClick={() => {
            if (!playerId) return;
            if (checkable) emit({ type: 'check', playerId });
            else if (callable) emit({ type: 'call', playerId, amount: callAmount });
          }}
          data-testid="action-check-call"
        >
          {checkable ? (
            <span className="text-sm">Check</span>
          ) : callable ? (
            <>
              <span className="text-sm">
                Call <span className="num">{callAmount}</span>
              </span>
              {bbSize > 0 ? (
                <span className="text-[9px] opacity-80">{toBB(callAmount, bbSize)}</span>
              ) : null}
            </>
          ) : (
            <span className="text-sm">Check / Call</span>
          )}
        </button>
        <button
          type="button"
          className="relative rounded-sm py-2 font-display uppercase tracking-widest border-2 border-warning bg-warning text-bg-deeper enabled:hover:brightness-110 enabled:active:translate-y-0.5 disabled:opacity-30 disabled:border-border disabled:bg-bg disabled:text-muted transition-all shadow-[3px_3px_0_0_hsl(var(--color-warning)/0.8)] enabled:hover:shadow-[4px_4px_0_0_hsl(var(--color-warning)/0.8)] enabled:active:shadow-[1px_1px_0_0_hsl(var(--color-warning)/0.5)] disabled:shadow-none flex flex-col items-center justify-center gap-0.5"
          disabled={!canRaise}
          onClick={submitAggressive}
          data-testid="action-raise"
        >
          {aggressive ? (
            <>
              <span className="text-sm">
                {aggressive.type === 'bet' ? 'Bet' : 'Raise'} <span className="num">{raiseTo}</span>
              </span>
              {bbSize > 0 ? (
                <span className="text-[9px] opacity-80">{toBB(raiseTo, bbSize)}</span>
              ) : null}
            </>
          ) : (
            <span className="text-sm">Raise</span>
          )}
        </button>
      </div>
      <button
        type="button"
        className="relative rounded-sm py-1.5 font-display text-sm uppercase tracking-[0.3em] border-2 border-accent bg-accent text-bg-deeper enabled:hover:brightness-110 enabled:active:translate-y-0.5 disabled:opacity-30 disabled:border-border disabled:bg-bg disabled:text-muted transition-all glow-blood disabled:shadow-none flex items-center justify-center gap-2"
        onClick={() =>
          canAllin && playerId ? emit({ type: 'allin', playerId, amount: allinAmount }) : undefined
        }
        disabled={!canAllin}
        data-testid="action-allin"
      >
        <span>★ All-IN</span>
        {canAllin && allinAmount > 0 ? <span className="num text-sm">{allinAmount}</span> : null}
        <span>★</span>
      </button>
    </footer>
  );
}
